import React, { useState, useEffect, useMemo } from 'react';
import { apiCall } from '../config/api';
import LoadingSpinner from '../components/LoadingSpinner';

export interface Licenca {
  grau: string;
  valida: string;
  num_licenca: string;
  num_registo: string;
  nome_cao: string;
  nome_registo: string;
  raca: string;
  proprietario: string;
  data_inicio_g1: string;
  data_pagamento: string;
  validade: string;
  validade_iso: string | null;
  classe: string;
  medicao_efetuada: string;
  ativa: boolean;
}

const LicencasPage: React.FC = () => {
  const [licencas, setLicencas] = useState<Licenca[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'ativas' | 'inativas'>('all');
  const [teamAceChecked, setTeamAceChecked] = useState(false);
  const [showFiltersMobile, setShowFiltersMobile] = useState(false);
  const [selected, setSelected] = useState<Licenca | null>(null);

  useEffect(() => {
    loadLicencas();
  }, []);

  const loadLicencas = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set('q', search.trim());
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (teamAceChecked) params.set('team', 'ace');
      const res = await apiCall('/licencas' + (params.toString() ? '?' + params.toString() : ''), { method: 'GET' });
      setLicencas(res.licencas || []);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar licenças.');
      setLicencas([]);
    } finally {
      setLoading(false);
    }
  };

  const isFirstRun = React.useRef(true);
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    const t = setTimeout(loadLicencas, 300);
    return () => clearTimeout(t);
  }, [search, statusFilter, teamAceChecked]);

  const formatValidade = (val: string) => {
    if (!val || val.toUpperCase() === 'N/A') return '—';
    return val;
  };

  if (loading && licencas.length === 0) {
    return (
      <div className="container py-4">
        <div className="text-center py-5">
          <LoadingSpinner />
          <p className="text-muted mt-2">A carregar licenças...</p>
        </div>
      </div>
    );
  }

  if (error && licencas.length === 0) {
    return (
      <div className="container py-4">
        <div className="alert alert-warning shadow-sm">
          <h5 className="alert-heading">
            <i className="fas fa-id-card me-2"></i>
            Licenças Agility
          </h5>
          <p className="mb-0">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="container py-3 py-md-4" style={{ paddingBottom: '3rem' }}>
        <h4 className="mb-3 mb-md-4" style={{ color: '#a2449c' }}>
          <i className="fas fa-id-card me-2"></i>
          Licenças Agility
        </h4>

        <div className="card shadow-sm mb-4">
          <div
            className="card-header py-2 d-md-none"
            style={{ cursor: 'pointer' }}
            onClick={() => setShowFiltersMobile((v) => !v)}
            role="button"
            aria-expanded={showFiltersMobile}
            aria-label={showFiltersMobile ? 'Ocultar filtros' : 'Mostrar filtros'}
          >
            <h6 className="mb-0 text-muted d-flex align-items-center">
              <i className="fas fa-filter me-2"></i>
              Pesquisa e filtros
              <i className={`fas fa-chevron-${showFiltersMobile ? 'up' : 'down'} ms-auto mt-1`}></i>
            </h6>
          </div>
          <div className={`card-body p-3 ${showFiltersMobile ? '' : 'd-none'} d-md-block`}>
            <div className="row g-2 g-md-3">
              <div className="col-12 col-md-5">
                <label className="form-label small text-muted mb-1">
                  Pesquisar (Nº Licença, Nº Registo, Nome do cão, Nome de Registo, Proprietário)
                </label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Digite para pesquisar..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="col-12 col-md-3">
                <label className="form-label small text-muted mb-1">Estado</label>
                <select
                  className="form-select form-select-sm"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as 'all' | 'ativas' | 'inativas')}
                >
                  <option value="all">Todas</option>
                  <option value="ativas">Ativas</option>
                  <option value="inativas">Inativas</option>
                </select>
              </div>
              <div className="col-12 col-md-3 d-flex align-items-end pb-2 pb-md-0">
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="team-ace-filter"
                    checked={teamAceChecked}
                    onChange={(e) => setTeamAceChecked(e.target.checked)}
                  />
                  <label className="form-check-label small text-muted" htmlFor="team-ace-filter">
                    Ace Agility Team
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        {licencas.length === 0 ? (
          <div className="card shadow-sm">
            <div className="card-body text-center py-5">
              <i className="fas fa-id-card fa-3x mb-3" style={{ color: '#a2449c' }}></i>
              <p className="text-muted mb-0">
                Nenhuma licença encontrada. Ajuste a pesquisa ou execute a sincronização em /sync-external-data.
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="d-md-none">
              {licencas.map((lic, idx) => (
                <div
                  key={`${lic.num_licenca}-${lic.num_registo}-${idx}`}
                  className="card shadow-sm mb-3"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelected(lic)}
                >
                  <div className="card-body p-3">
                    <div className="fw-bold" style={{ color: '#a2449c' }}>{lic.nome_cao || '—'}</div>
                    <div className="small text-muted">
                      {lic.proprietario && <span>Proprietário: {lic.proprietario}</span>}
                      {lic.proprietario && lic.grau && ' · '}
                      {lic.grau && <span>Grau: {lic.grau}</span>}
                    </div>
                    <div className="small text-muted">
                      Nome de Registo: {lic.nome_registo || '—'} · Nº Licença: {lic.num_licenca || '—'}
                    </div>
                    <div className="small mt-1">
                      Validade: {formatValidade(lic.validade)} ·{' '}
                      <span className={lic.ativa ? 'text-success fw-bold' : 'text-danger'}>
                        {lic.ativa ? 'Válida' : 'Não válida'}
                      </span>
                    </div>
                    <i className="fas fa-chevron-right float-end mt-1 text-muted small"></i>
                  </div>
                </div>
              ))}
            </div>
            <div className="d-none d-md-block card shadow-sm">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead style={{ backgroundColor: '#f8f9fa' }}>
                    <tr>
                      <th>Nome do cão</th>
                      <th>Proprietário</th>
                      <th>Nome de Registo</th>
                      <th>Grau</th>
                      <th>Nº Licença</th>
                      <th>Validade</th>
                      <th className="text-center">Estado</th>
                      <th style={{ width: '40px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {licencas.map((lic, idx) => (
                      <tr
                        key={`${lic.num_licenca}-${lic.num_registo}-${idx}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setSelected(lic)}
                        className="align-middle"
                      >
                        <td className="fw-bold" style={{ color: '#a2449c' }}>{lic.nome_cao || '—'}</td>
                        <td>{lic.proprietario || '—'}</td>
                        <td>{lic.nome_registo || '—'}</td>
                        <td>{lic.grau || '—'}</td>
                        <td>{lic.num_licenca || '—'}</td>
                        <td>{formatValidade(lic.validade)}</td>
                        <td className="text-center">
                          <span className={lic.ativa ? 'badge bg-success' : 'badge bg-secondary'}>
                            {lic.ativa ? 'Válida' : 'Não válida'}
                          </span>
                        </td>
                        <td><i className="fas fa-chevron-right text-muted small"></i></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="mt-3 text-muted small">
              {licencas.length} {licencas.length === 1 ? 'licença' : 'licenças'}
            </div>
          </>
        )}
      </div>

      {selected && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={() => setSelected(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="licencaDetailLabel"
        >
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: '#a2449c', color: 'white' }}>
                <h5 className="modal-title" id="licencaDetailLabel">
                  <i className="fas fa-id-card me-2"></i>
                  {selected.nome_cao || 'Licença'}
                  <span className="badge bg-light text-dark ms-2">{selected.grau}</span>
                  {selected.ativa && <span className="badge bg-success ms-1">Válida</span>}
                </h5>
                <button type="button" className="btn-close btn-close-white" aria-label="Fechar" onClick={() => setSelected(null)} />
              </div>
              <div className="modal-body">
                <dl className="row mb-0 small">
                  <dt className="col-sm-4 text-muted">Nº Licença</dt>
                  <dd className="col-sm-8 mb-2">{selected.num_licenca || '—'}</dd>
                  <dt className="col-sm-4 text-muted">Nº Registo</dt>
                  <dd className="col-sm-8 mb-2">{selected.num_registo || '—'}</dd>
                  <dt className="col-sm-4 text-muted">Nome do cão</dt>
                  <dd className="col-sm-8 mb-2">{selected.nome_cao || '—'}</dd>
                  <dt className="col-sm-4 text-muted">Nome de Registo</dt>
                  <dd className="col-sm-8 mb-2">{selected.nome_registo || '—'}</dd>
                  <dt className="col-sm-4 text-muted">Raça</dt>
                  <dd className="col-sm-8 mb-2">{selected.raca || '—'}</dd>
                  <dt className="col-sm-4 text-muted">Proprietário</dt>
                  <dd className="col-sm-8 mb-2">{selected.proprietario || '—'}</dd>
                  <dt className="col-sm-4 text-muted">Validade</dt>
                  <dd className="col-sm-8 mb-2">{formatValidade(selected.validade)}</dd>
                  <dt className="col-sm-4 text-muted">Data Início G1</dt>
                  <dd className="col-sm-8 mb-2">{selected.data_inicio_g1 || '—'}</dd>
                  <dt className="col-sm-4 text-muted">Data Pagamento</dt>
                  <dd className="col-sm-8 mb-2">{selected.data_pagamento || '—'}</dd>
                  <dt className="col-sm-4 text-muted">Classe</dt>
                  <dd className="col-sm-8 mb-2">{selected.classe || '—'}</dd>
                  <dt className="col-sm-4 text-muted">Medição Efetuada</dt>
                  <dd className="col-sm-8 mb-2">{selected.medicao_efetuada || '—'}</dd>
                </dl>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setSelected(null)}>Fechar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <footer
        className="text-center py-2 border-top bg-light"
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          fontSize: '0.75rem',
          color: '#6c757d',
          zIndex: 1020,
        }}
      >
        Esta informação é da total responsabilidade do CPC Agility
      </footer>
    </>
  );
};

export default LicencasPage;
